/**
 * 예식 시각(`invitation.weddingAt`) 하나에서 화면에 쓰이는 모든 날짜 문구를 만든다.
 *
 * 스펙 §7.4는 달력이 날짜 변경을 견뎌야 한다고 못박는다. 달력 격자만 계산하고
 * 연·월·일과 날짜 줄을 리터럴로 두면, 날짜를 바꾸는 순간 한 섹션의 두 반쪽이
 * 서로 다른 말을 한다 — 그 상태가 하객에게 보이는 것이 최악이다.
 */
const KST_OFFSET_MS = 9 * 60 * 60 * 1000

const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토']
const WEEKDAY_EN = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
const MONTH_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export interface WeddingMoment {
  year: number
  /** 1부터 센다. */
  month: number
  day: number
  /** 0=일 … 6=토 */
  weekday: number
  /** 0–23 */
  hour: number
  minute: number
}

/**
 * 하객의 기기 시간대와 무관하게 항상 한국시간으로 읽는다.
 * `getFullYear()` 같은 로컬 접근자를 쓰면 로스앤젤레스의 하객에게는 예식이
 * 하루 전날로 보인다 — `dDayLabel`이 이미 쓰는 것과 같은 고정 오프셋 방식이다.
 */
export function weddingMoment(iso: string): WeddingMoment {
  const kst = new Date(new Date(iso).getTime() + KST_OFFSET_MS)
  return {
    year: kst.getUTCFullYear(),
    month: kst.getUTCMonth() + 1,
    day: kst.getUTCDate(),
    weekday: kst.getUTCDay(),
    hour: kst.getUTCHours(),
    minute: kst.getUTCMinutes(),
  }
}

function hour12(hour: number): number {
  const value = hour % 12
  return value === 0 ? 12 : value
}

const pad2 = (value: number): string => String(value).padStart(2, '0')

/** '2026. 11. 21. SAT' — 히어로 배지와 푸터가 함께 쓴다. */
export function shortDateLabel(m: WeddingMoment): string {
  return `${m.year}. ${m.month}. ${m.day}. ${WEEKDAY_EN[m.weekday]}`
}

/** '토요일 오전 11시' */
export function koreanTimeLine(m: WeddingMoment): string {
  const minutes = m.minute === 0 ? '' : ` ${m.minute}분`
  return `${WEEKDAY_KO[m.weekday]}요일 ${m.hour < 12 ? '오전' : '오후'} ${hour12(m.hour)}시${minutes}`
}

/** '2026년 11월 21일 토요일 오전 11시' */
export function koreanDateLine(m: WeddingMoment): string {
  return `${m.year}년 ${m.month}월 ${m.day}일 ${koreanTimeLine(m)}`
}

/** 'AM 11:00' — 푸터 한 줄. */
export function clockLabel(m: WeddingMoment): string {
  return `${m.hour < 12 ? 'AM' : 'PM'} ${hour12(m.hour)}:${pad2(m.minute)}`
}

/** 'November' — 달력 머리글. */
export function monthNameEn(m: WeddingMoment): string {
  return MONTH_EN[m.month - 1] ?? ''
}
